import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {KTIcon} from '../../../../../general/helpers'
import Modal from '../../components/Modal'
import {create_store} from '../../core/_request'
import {CreateStoreFilterI, FilterI} from '../../core/_models'
import Input from '../../../auth/components/Input'
import Select from '../../../auth/components/Select'
import {setPropertiesOptionsArr, parseJwt} from '../../core/helper'
import CardLoading from '../../components/CardLoading'
import {useStoresQueryRequest} from '../core/StoresRequestProvider'
import {useStoresQueryResponseLoading} from '../core/StoresResponseProvider'
import {getCreateStoreProperties} from './constants'
import {useAuth} from '../../../auth'
import {useStoresContext} from '../core/Stores'
import {useNavigate} from 'react-router-dom'

const StoresListCreateButton = () => {
  const [storeDialogOpen, setNewStoreOpen] = useState<boolean>(false)
  const intl = useIntl()
  const [loading, setLoading] = useState<boolean>(false)
  const [propertyData, setPropertyData] = useState<CreateStoreFilterI[]>([])
  const [properties, setProperties] = useState<{[key: string]: string}>({})
  const storesloading = useStoresQueryResponseLoading()
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string | null}>({})
  const {auth} = useAuth()
  const {activeRetailer} = parseJwt(auth?.accessToken ?? '')
  const [saving, setSaving] = useState(false)
  const {triggerRefreshStoresList} = useStoresContext()
  /**
   * fetching filters for stores
   */
  const fetchProperties = async () => {
    setLoading(true)
    try {
      const response: CreateStoreFilterI[] = getCreateStoreProperties(activeRetailer)
      setPropertyData(response.sort((a, b) => a.order - b.order))
    } catch {
    } finally {
      setLoading(false)
    }
  }

  const validateField = (slug: string, value: any): string | null => {
    const populated = (val: any) => val !== undefined && val !== null && val !== ''

    switch (slug) {
      case 'refNumber':
        if (!populated(value)) return intl.formatMessage({id: 'STORES.ERROR.REQUIRED_FIELD'})
        if (!/^\d+$/.test(value)) return intl.formatMessage({id: 'STORES.ERROR.DIGITS_ONLY'})
        if (value.length > 20)
          return intl.formatMessage({id: 'STORES.ERROR.MAX_LENGTH'}, {length: 20})
        break

      case 'storeGln':
        if (!populated(value)) return intl.formatMessage({id: 'STORES.ERROR.REQUIRED_FIELD'})
        if (!/^\d+$/.test(value)) return intl.formatMessage({id: 'STORES.ERROR.DIGITS_ONLY'})
        if (value.length > 20)
          return intl.formatMessage({id: 'STORES.ERROR.MAX_LENGTH'}, {length: 20})
        break

      case 'name':
        if (!populated(value)) return intl.formatMessage({id: 'STORES.ERROR.REQUIRED_FIELD'})
        break

      case 'language':
      case 'countryCode':
        if (!populated(value)) return intl.formatMessage({id: 'STORES.ERROR.REQUIRED_FIELD'})
        if (!/^[A-Za-z]{2}$/.test(value)) {
          return intl.formatMessage({id: 'STORES.ERROR.INVALID_COUNTRY_CODE'})
        }
        if (value.length > 2)
          return intl.formatMessage({id: 'STORES.ERROR.MAX_LENGTH'}, {length: 2})
        break

      case 'countryName':
        if (value && value.length > 100) {
          return intl.formatMessage({id: 'STORES.ERROR.MAX_LENGTH'}, {length: 100})
        }
        break

      /*case 'dailyTradingLimit':
        if(populated(value)){
          if (!populated(value)) return intl.formatMessage({id: 'STORES.ERROR.REQUIRED_FIELD'});
          if (!/^\d+$/.test(value)) return intl.formatMessage({id: 'STORES.ERROR.DIGITS_ONLY'});
          // Simulate long max (2^63 - 1): 9223372036854775807
          if (value.length > 19 || BigInt(value) > BigInt("9223372036854775807")) {
            return intl.formatMessage({id: 'STORES.ERROR.MAX_LONG'});
          }
        }
        break;
  
      case 'sapServiceTags':
        if (value && value.length > 1024) {
          return intl.formatMessage({id: 'STORES.ERROR.MAX_LENGTH'}, {length: 1024});
        }
        break;*/

      default:
        // Optional fields: streetName, houseNumber, city, postalCode, stateCode, stateName, operatorModel
        return null
    }

    return null // No error
  }

  const checkNewStoreForErrors = (form) => {
    const properties: CreateStoreFilterI[] = getCreateStoreProperties(activeRetailer)
    const errors: {[key: string]: string | null} = {}
    let hasErrors = false
    for (let property of properties) {
      const value = form[property.slug]
      const error = validateField(property.slug, form[property.slug])
      errors[property.slug] = error
      if (error) hasErrors = true
    }
    return errors
  }

  const addStore = async () => {
    //check for validations
    const storeForm = properties
    const errors = checkNewStoreForErrors(storeForm)
    const hasErrors = Object.values(errors).some((msg) => msg !== null)
    if (!hasErrors) {
      setSaving(true)
      try {
        const {activeRetailer} = parseJwt(auth?.accessToken ?? '')

        await create_store({
          refNumber: storeForm.refNumber,
          storeGln: storeForm.storeGln,
          name: storeForm.name,
          streetName: storeForm.streetName ?? '',
          houseNumber: storeForm.houseNumber ?? '',
          city: storeForm.city ?? '',
          postalCode: storeForm.postalCode ?? '',
          stateCode: storeForm.stateCode ?? '',
          stateName: storeForm.stateName ?? '',
          countryCode: storeForm.countryCode ?? '',
          countryName: storeForm.countryName ?? '',
          language: storeForm.language,
          storeFormat: storeForm.storeFormat,
          operatorModel: storeForm.operatorModel,
          enabled: true,
          dailyTradingLimit: storeForm.dailyTradingLimit,
          sapServiceTags: storeForm.sapServiceTags,
          retailer: {id: activeRetailer?.id},
        }).catch(() => {})
      } catch (error) {
        console.error(error)
      } finally {
        setSaving(false)
        triggerRefreshStoresList()
        setFieldErrors({})
        setNewStoreOpen(false)
      }
    } else {
      setFieldErrors(errors) // Display errors
    }
  }
  /**
   * resets all the filters applied
   */
  const cancelCreation = () => {
    setFieldErrors({}) // Reset the error state
    setNewStoreOpen(false)
  }

  const initializeDefaultProperties = () => {
    const defaults: {[key: string]: string} = {}
    propertyData.forEach((item) => {
      if (item.default !== undefined && item.default !== null) {
        defaults[item.slug] = item.default
      }
    })
    setProperties(defaults)
  }

  useEffect(() => {
    fetchProperties()
  }, [])

  useEffect(() => {
    if (storeDialogOpen && propertyData.length > 0) {
      initializeDefaultProperties()
    }

    if (!storeDialogOpen) {
      setProperties({})
      setFieldErrors({})
    }
  }, [storeDialogOpen, propertyData])

  useEffect(() => {
    setFieldErrors({}) // Reset the error state
    !storeDialogOpen && setProperties({})
    // eslint-disable-next-line
  }, [storeDialogOpen])
  return (
    <>
      <button
        disabled={storesloading}
        type='button'
        className='btn btn-light-primary me-3'
        onClick={() => setNewStoreOpen((prev) => !prev)}
      >
        <KTIcon iconName='filter' className='fs-2' />
        {intl.formatMessage({id: 'STORES.NEW'})}
      </button>

      {storeDialogOpen ? (
        <Modal
          heading={intl.formatMessage({id: 'STORES.NEW'})}
          onClose={() => {
            setNewStoreOpen(false)
          }}
          size='mw-750px'
        >
          <div className='row align-items-center'>
            {loading ? (
              <CardLoading />
            ) : (
              <>
                {propertyData.map((filterItem) => (
                  <div className=' col-md-4 col-sm-12 col-xs-12 mb-8' key={filterItem?.slug}>
                    {filterItem.data?.length > 0 ? (
                      <Select
                        label={filterItem.isMandatory ? `${filterItem.name} *` : filterItem.name}
                        placeHolder={filterItem.name}
                        attrs={{
                          name: filterItem.name,
                          value: properties?.[filterItem.slug] ?? filterItem.default ?? '',
                        }}
                        disabled={filterItem.disabled ?? false}
                        onChange={(e) => {
                          setProperties((prev) => ({...prev, [filterItem.slug]: e.target.value}))
                          setFieldErrors((prev) => ({...prev, [filterItem.slug]: null}))
                        }}
                        options={setPropertiesOptionsArr(filterItem?.data)}
                        isClearable={filterItem.disabled ?? true}
                        onClear={() => {
                          setProperties((prev) => ({...prev, [filterItem.slug]: ''}))
                        }}
                      />
                    ) : (
                      <Input
                        label={filterItem.isMandatory ? `${filterItem.name} *` : filterItem.name}
                        placeHolder={filterItem.name}
                        attrs={{
                          name: filterItem.name,
                          value: properties?.[filterItem.slug] ?? filterItem.default ?? '',
                        }}
                        onChange={(value, e) => {
                          setProperties((prev) => ({...prev, [filterItem.slug]: value}))
                          setFieldErrors((prev) => ({...prev, [filterItem.slug]: null}))
                        }}
                        disabled={filterItem.disabled ?? false}
                        isClearable={filterItem.disabled ? false : true}
                      />
                    )}
                    {fieldErrors[filterItem.slug] && (
                      <div className='text-danger mt-1'>{fieldErrors[filterItem.slug]}</div>
                    )}
                  </div>
                ))}
                <div className='row gap-5 justify-content-end'>
                  <button className={'col-md-2 btn btn-light'} onClick={cancelCreation}>
                    <span className='indicator-label'>{intl.formatMessage({id: 'CANCEL'})}</span>
                  </button>
                  <button
                    className='col-md-2 btn btn-primary'
                    style={{height: '100%', minHeight: 'min-content'}}
                    onClick={addStore}
                    disabled={saving}
                  >
                    {saving && (
                      <span className='spinner-border spinner-border-sm align-middle me-2' />
                    )}
                    {intl.formatMessage({id: 'ADD'})}
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      ) : (
        <></>
      )}
    </>
  )
}

export default StoresListCreateButton
